import {
  createAdHocIconRole,
  getIconByRole,
  Icon,
  IconRole,
  IconStyle,
  iconStyles,
  initializeIconByRole,
  NO_ICON_CONFIGURED,
  NoIconConfigured,
} from '@cloudsuite/mosaic-shared/icons/library.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import {
  faBrands,
  faBrandsClass,
  faLightClass,
  faMain,
  faRegularClass,
  faSolidClass,
  faThinClass
} from 'elements/icon/font-awesome/current.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { style } from './icon.style.js'
import { RENDER_ICON, RenderIcon } from './render-icon.js'


export const iconSizes = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'] as const
export type IconSize = typeof iconSizes[number]

function isValidIconName(iconName: string | null): iconName is string {
  return iconName !== null
}

function isValidIconStyle(iconStyle: string | null): iconStyle is IconStyle {
  return iconStyle !== null && iconStyles.includes(iconStyle as IconStyle)
}

function isValidIconRole(iconRole: string | null): iconRole is IconRole {
  return iconRole !== null
}

function isConfiguredIcon(icon: Icon | NoIconConfigured): icon is Icon {
  return icon !== NO_ICON_CONFIGURED
}

function hasCustomIcon(slot: HTMLSlotElement | null): boolean {
  return Boolean(slot?.assignedElements().length)
}


@customElement('cse-icon')
export class CSEIcon extends LitElement {
  static override styles: CSSResultGroup =
    [hostStyle, style, faMain, faLightClass, faRegularClass, faSolidClass, faThinClass, faBrandsClass, faBrands]

  @inject(RENDER_ICON)
  private iconService!: RenderIcon

  @property({ attribute: 'icon-hidden', reflect: true, type: Boolean })
  iconHidden: boolean = true

  @property({ reflect: true })
  size: IconSize = 'md'

  @property({ attribute: 'icon-role', reflect: true })
  iconRole: string | null = null

  @property({ attribute: 'icon-name' })
  iconName: string | null = null

  @property({ attribute: 'icon-style' })
  iconStyle: string | null = null

  @state()
  private icon: Icon | NoIconConfigured = NO_ICON_CONFIGURED

  @query('slot')
  private customIconSlot!: HTMLSlotElement | null

  override connectedCallback() {
    super.connectedCallback()
    this.resolveIcon()
  }

  override disconnectedCallback() {
    this.disposeIconSubscription?.()
    super.disconnectedCallback()
  }

  protected override update(changedProperties: PropertyValues<this>) {
    const shouldResolveIcon = changedProperties.has('iconRole')
      || changedProperties.has('iconName')
      || changedProperties.has('iconStyle')

    if (shouldResolveIcon) { this.resolveIcon() }

    super.update(changedProperties)
  }

  private disposeIconSubscription: (() => void) | null = null

  private resolveIcon = (): void => {
    this.disposeIconSubscription?.()

    // Ad-hoc icon takes precedence
    if (isValidIconName(this.iconName) && isValidIconStyle(this.iconStyle)) {
      const iconRole = createAdHocIconRole()
      initializeIconByRole(iconRole, { name: this.iconName, style: this.iconStyle })
      this.subscribeToIconRole(iconRole)
      return
    }

    // Default role-based icon
    if (isValidIconRole(this.iconRole)) {
      this.subscribeToIconRole(this.iconRole)
      return
    }

    this.icon = NO_ICON_CONFIGURED
    this.updateIconDisplay()
  }

  private subscribeToIconRole = (iconRole: IconRole) => {
    const iconGetter = getIconByRole(iconRole)
    this.disposeIconSubscription = createEffect(() => {
      this.icon = iconGetter()
      this.updateIconDisplay()
    })
  }

  private updateIconDisplay = () => {
    this.iconHidden = !isConfiguredIcon(this.icon) && !hasCustomIcon(this.customIconSlot)
  }

  protected override render(): TemplateResult {
    const iconContent = isConfiguredIcon(this.icon)
      ? this.iconService.renderIcon(this.icon)
      : nothing

    return html`<slot @slotchange=${this.updateIconDisplay}>${iconContent}</slot>`
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-icon': CSEIcon
  }
}
